import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";

const defaultColumns = [
  { key: "label", label: "Label", type: "text", locked: true },
  { key: "value", label: "Value", type: "number", locked: true },
];

export const emptyChart = (order = 0) => ({
  type: "bar",
  title: "",
  subtitle: "",
  source: "",
  order,
  columns: defaultColumns,
  data: [{ label: "", value: "" }],
});

const normalizeColumns = (columns) => {
  const customColumns = Array.isArray(columns)
    ? columns.filter(
        (column) =>
          column?.key &&
          defaultColumns.every((defaultColumn) => defaultColumn.key !== column.key)
      )
    : [];

  return [
    ...defaultColumns,
    ...customColumns.map((column) => ({
      key: column.key,
      label: column.label || column.key,
      type: column.type === "number" ? "number" : "text",
    })),
  ];
};

const emptyRowForColumns = (columns) =>
  columns.reduce((row, column) => {
    row[column.key] = "";
    return row;
  }, {});

const normalizeChart = (chart, index) => ({
  ...emptyChart(index + 1),
  ...chart,
  columns: normalizeColumns(chart?.columns),
  data:
    Array.isArray(chart?.data) && chart.data.length > 0
      ? chart.data
      : [{ label: "", value: "" }],
});

const BlogChartBuilder = ({ charts = [], onChange, compact = false }) => {
  const updateCharts = (nextCharts) => {
    onChange(nextCharts.map((chart, index) => ({ ...chart, order: index + 1 })));
  };

  const handleChartChange = (index, field, value) => {
    const nextCharts = charts.map((chart, chartIndex) =>
      chartIndex === index ? { ...normalizeChart(chart, index), [field]: value } : chart
    );
    updateCharts(nextCharts);
  };

  const handleRowChange = (chartIndex, rowIndex, field, value) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;

      const normalizedChart = normalizeChart(chart, chartIndex);
      const data = normalizedChart.data.map((row, currentRowIndex) =>
        currentRowIndex === rowIndex ? { ...row, [field]: value } : row
      );

      return { ...normalizedChart, data };
    });
    updateCharts(nextCharts);
  };

  const handleColumnChange = (chartIndex, columnIndex, field, value) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;

      const normalizedChart = normalizeChart(chart, chartIndex);
      const columns = normalizedChart.columns.map((column, currentColumnIndex) =>
        currentColumnIndex === columnIndex ? { ...column, [field]: value } : column
      );

      return { ...normalizedChart, columns };
    });
    updateCharts(nextCharts);
  };

  const addChart = () => {
    updateCharts([...charts, emptyChart(charts.length + 1)]);
  };

  const removeChart = (index) => {
    updateCharts(charts.filter((_, chartIndex) => chartIndex !== index));
  };

  const addRow = (chartIndex) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;
      const normalizedChart = normalizeChart(chart, chartIndex);
      return {
        ...normalizedChart,
        data: [...normalizedChart.data, emptyRowForColumns(normalizedChart.columns)],
      };
    });
    updateCharts(nextCharts);
  };

  const removeRow = (chartIndex, rowIndex) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;

      const normalizedChart = normalizeChart(chart, chartIndex);
      return {
        ...normalizedChart,
        data:
          normalizedChart.data.length > 1
            ? normalizedChart.data.filter((_, currentRowIndex) => currentRowIndex !== rowIndex)
            : normalizedChart.data,
      };
    });
    updateCharts(nextCharts);
  };

  const addColumn = (chartIndex) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;

      const normalizedChart = normalizeChart(chart, chartIndex);
      const nextIndex = normalizedChart.columns.length + 1;
      const nextColumn = {
        key: `column_${Date.now()}`,
        label: `Column ${nextIndex}`,
        type: "number",
      };

      return {
        ...normalizedChart,
        columns: [...normalizedChart.columns, nextColumn],
        data: normalizedChart.data.map((row) => ({ ...row, [nextColumn.key]: "" })),
      };
    });
    updateCharts(nextCharts);
  };

  const removeColumn = (chartIndex, columnIndex) => {
    const nextCharts = charts.map((chart, currentChartIndex) => {
      if (currentChartIndex !== chartIndex) return chart;

      const normalizedChart = normalizeChart(chart, chartIndex);
      const removingColumn = normalizedChart.columns[columnIndex];
      if (!removingColumn || removingColumn.locked) return normalizedChart;

      return {
        ...normalizedChart,
        columns: normalizedChart.columns.filter(
          (_, currentColumnIndex) => currentColumnIndex !== columnIndex
        ),
        data: normalizedChart.data.map((row) => {
          const nextRow = { ...row };
          delete nextRow[removingColumn.key];
          return nextRow;
        }),
      };
    });
    updateCharts(nextCharts);
  };

  return (
    <div className={compact ? "" : "border-top pt-3 mt-2"}>
      {!compact && (
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="mb-0">Charts</h5>
          <Button variant="outline-primary" type="button" onClick={addChart}>
            Add Chart
          </Button>
        </div>
      )}

      {!compact && charts.length === 0 && (
        <p className="text-muted mb-3">No chart blocks added.</p>
      )}

      {charts.map((chart, chartIndex) => {
        const normalizedChart = normalizeChart(chart, chartIndex);

        return (
          <div className="border rounded p-3 mb-3" key={`chart-${chartIndex}`}>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={normalizedChart.type}
                    onChange={(event) =>
                      handleChartChange(chartIndex, "type", event.target.value)
                    }
                  >
                    <option value="bar">Bar chart</option>
                    <option value="line">Line chart</option>
                    <option value="pie">Pie chart</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    type="text"
                    value={normalizedChart.title}
                    onChange={(event) =>
                      handleChartChange(chartIndex, "title", event.target.value)
                    }
                    placeholder="Chart title"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Subtitle</Form.Label>
                  <Form.Control
                    type="text"
                    value={normalizedChart.subtitle}
                    onChange={(event) =>
                      handleChartChange(chartIndex, "subtitle", event.target.value)
                    }
                    placeholder="Optional subtitle"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Source</Form.Label>
                  <Form.Control
                    type="text"
                    value={normalizedChart.source}
                    onChange={(event) =>
                      handleChartChange(chartIndex, "source", event.target.value)
                    }
                    placeholder="Optional data source"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="mb-2">
              <b>Data Columns</b>
            </div>
            {normalizedChart.columns.map((column, columnIndex) => (
              <Row
                className="align-items-end mb-2"
                key={`chart-${chartIndex}-column-${column.key}`}
              >
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Column Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={column.label || ""}
                      onChange={(event) =>
                        handleColumnChange(
                          chartIndex,
                          columnIndex,
                          "label",
                          event.target.value
                        )
                      }
                      placeholder="Column name"
                      disabled={column.locked}
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Column Type</Form.Label>
                    <Form.Select
                      value={column.type || "text"}
                      onChange={(event) =>
                        handleColumnChange(
                          chartIndex,
                          columnIndex,
                          "type",
                          event.target.value
                        )
                      }
                      disabled={column.locked}
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    type="button"
                    className="w-100"
                    onClick={() => removeColumn(chartIndex, columnIndex)}
                    disabled={column.locked}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="mb-2 mt-3">
              <b>Data Rows</b>
            </div>
            {normalizedChart.data.map((row, rowIndex) => (
              <Row className="align-items-end mb-2" key={`chart-${chartIndex}-row-${rowIndex}`}>
                {normalizedChart.columns.map((column) => (
                  <Col md={column.key === "label" ? 4 : 3} key={column.key}>
                    <Form.Group>
                      <Form.Label>{column.label}</Form.Label>
                      <Form.Control
                        type={column.type === "number" ? "number" : "text"}
                        value={row[column.key] || ""}
                        onChange={(event) =>
                          handleRowChange(chartIndex, rowIndex, column.key, event.target.value)
                        }
                        placeholder={column.label}
                      />
                    </Form.Group>
                  </Col>
                ))}
                <Col md={2}>
                  <Button
                    variant="outline-danger"
                    type="button"
                    className="w-100"
                    onClick={() => removeRow(chartIndex, rowIndex)}
                    disabled={normalizedChart.data.length === 1}
                  >
                    Remove
                  </Button>
                </Col>
              </Row>
            ))}

            <div className="d-flex justify-content-between mt-3">
              <div className="d-flex gap-2">
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => addRow(chartIndex)}
                >
                  Add Row
                </Button>
                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => addColumn(chartIndex)}
                >
                  Add Column
                </Button>
              </div>
              {!compact && (
                <Button
                  variant="outline-danger"
                  type="button"
                  onClick={() => removeChart(chartIndex)}
                >
                  Remove Chart
                </Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default BlogChartBuilder;
